import dayjs from 'dayjs';

export type QuoteOfferCallRow = {
  CallID: number; RetellCallID: string | null; QuoteID: number; QuoteOfferID: number;
  CreatedUtc: string; Reach: number; Response: number; RateRequested: boolean;
  DisconnectionReason: string | null; DurationMs: number | null; CostCents: number | null; DeclineReason: string | null;
  ResponseDetail: string | null; AlternatePickupUtc: string | null; AlternateDeliveryUtc: string | null;
  SecondsToAnswer: number | null; DriverNumber: string | null;
  DriverName: string | null; VehicleNumber: string | null; VehicleType: string | null;
};

export type QuoteOfferCallsSummary = { Calls: QuoteOfferCallRow[] };

// Mirrors QuoteOfferCallReach and QuoteOfferCallResponse in LoadOne.Agents.
export const REACH = { Placed: 0, Connected: 1, Voicemail: 2, NoAnswer: 3, Busy: 4, Failed: 5 };
export const RESPONSE = { None: 0, Accepted: 1, Declined: 2, AcceptedWithNewPickup: 3, LeftForApp: 4, WrongNumber: 5, Unclear: 6, AnsweredInApp: 7, OfferClosed: 8 };

export type Stage = 'reached' | 'voicemail' | 'noAnswer' | 'busy' | 'failed' | 'pending';

// A recorded response means a person was on the line, even if Retell's end-of-call report never arrived.
export function stageOf(r: QuoteOfferCallRow): Stage {
  if (r.Response !== RESPONSE.None || r.Reach === REACH.Connected) return 'reached';
  if (r.Reach === REACH.Voicemail) return 'voicemail';
  if (r.Reach === REACH.NoAnswer) return 'noAnswer';
  if (r.Reach === REACH.Busy) return 'busy';
  if (r.Reach === REACH.Failed) return 'failed';
  return 'pending';
}

export const isAccepted = (r: QuoteOfferCallRow) =>
  r.Response === RESPONSE.Accepted || r.Response === RESPONSE.AcceptedWithNewPickup;

const isAnswer = (r: QuoteOfferCallRow) =>
  isAccepted(r) || r.Response === RESPONSE.Declined || r.Response === RESPONSE.LeftForApp;

export function pct(part: number, whole: number): number | null {
  return whole > 0 ? Math.round((part / whole) * 1000) / 10 : null;
}

export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

export function inLocalRange(rows: QuoteOfferCallRow[], from: Date, to: Date): QuoteOfferCallRow[] {
  const start = dayjs(from).startOf('day').valueOf();
  const end = dayjs(to).endOf('day').valueOf();
  return rows.filter(r => {
    const t = dayjs(r.CreatedUtc).valueOf();
    return t >= start && t <= end;
  });
}

export function summarize(rows: QuoteOfferCallRow[]) {
  const count = (fn: (r: QuoteOfferCallRow) => boolean) => rows.filter(fn).length;
  const reachedRows = rows.filter(r => stageOf(r) === 'reached');
  const reached = reachedRows.length;
  const accepted = count(r => r.Response === RESPONSE.Accepted);
  const acceptedLater = count(r => r.Response === RESPONSE.AcceptedWithNewPickup);
  const declined = count(r => r.Response === RESPONSE.Declined);
  const leftForApp = count(r => r.Response === RESPONSE.LeftForApp);
  const unclear = count(r => r.Response === RESPONSE.Unclear || r.Response === RESPONSE.WrongNumber);
  const answeredInApp = count(r => r.Response === RESPONSE.AnsweredInApp);
  const offerClosed = count(r => r.Response === RESPONSE.OfferClosed);
  const rateRows = rows.filter(r => r.RateRequested);
  const costRows = rows.filter(r => r.CostCents != null);
  const costUsd = costRows.reduce((sum, r) => sum + (r.CostCents as number), 0) / 100;

  return {
    placed: rows.length,
    reached,
    voicemail: count(r => stageOf(r) === 'voicemail'),
    noAnswer: count(r => stageOf(r) === 'noAnswer'),
    busy: count(r => stageOf(r) === 'busy'),
    failed: count(r => stageOf(r) === 'failed'),
    pending: count(r => stageOf(r) === 'pending'),
    accepted,
    acceptedLater,
    declined,
    leftForApp,
    unclear,
    answeredInApp,
    offerClosed,
    noAnswerGiven: reached - accepted - acceptedLater - declined - leftForApp - unclear - answeredInApp - offerClosed,
    reachRate: pct(reached, rows.length),
    answerRate: pct(accepted + acceptedLater + declined + leftForApp, reached),
    acceptRate: pct(accepted + acceptedLater, reached),
    medianSecondsToAnswer: median(rows.filter(r => isAnswer(r) && r.SecondsToAnswer != null).map(r => r.SecondsToAnswer as number)),
    medianDurationMs: median(reachedRows.filter(r => r.DurationMs != null).map(r => r.DurationMs as number)),
    costUsd,
    costCalls: costRows.length,
    costPerCallUsd: costRows.length > 0 ? costUsd / costRows.length : null,
    costPerAcceptedUsd: accepted + acceptedLater > 0 && costRows.length > 0 ? costUsd / (accepted + acceptedLater) : null,
    rateRequested: rateRows.length,
    rateRequestedAccepted: rateRows.filter(isAccepted).length,
    rateRequestedDeclined: rateRows.filter(r => r.Response === RESPONSE.Declined).length,
  };
}

export function declineReasons(rows: QuoteOfferCallRow[]): { reason: string; count: number }[] {
  const counts = new Map<string, number>();
  rows.filter(r => r.Response === RESPONSE.Declined).forEach(r => {
    const reason = r.DeclineReason?.trim() || 'No reason given';
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  });
  return Array.from(counts, ([reason, count]) => ({ reason, count })).sort((a, b) => b.count - a.count);
}

function groupBy(rows: QuoteOfferCallRow[], keyOf: (r: QuoteOfferCallRow) => string) {
  const groups = new Map<string, QuoteOfferCallRow[]>();
  rows.forEach(r => {
    const key = keyOf(r);
    const calls = groups.get(key);
    if (calls) calls.push(r);
    else groups.set(key, [r]);
  });
  return groups;
}

function totals(calls: QuoteOfferCallRow[]) {
  const reached = calls.filter(r => stageOf(r) === 'reached').length;
  const accepted = calls.filter(isAccepted).length;
  return {
    calls: calls.length,
    reached,
    notReached: calls.length - reached,
    accepted,
    declined: calls.filter(r => r.Response === RESPONSE.Declined).length,
    acceptRate: pct(accepted, reached),
  };
}

export const unitOf = (r: QuoteOfferCallRow) => r.VehicleNumber?.trim() || `Driver ${r.DriverNumber ?? '?'}`;

/** One row per unit, newest call first inside each, units by truck number. */
export function byUnit(rows: QuoteOfferCallRow[]) {
  return Array.from(groupBy(rows, unitOf), ([unit, calls]) => {
    const newestFirst = [...calls].sort((a, b) => b.CreatedUtc.localeCompare(a.CreatedUtc));
    return {
      unit,
      vehicleType: calls.find(r => r.VehicleType)?.VehicleType ?? null,
      drivers: Array.from(new Set(calls.map(r => r.DriverName).filter(Boolean))).join(', '),
      ...totals(calls),
      latest: newestFirst[0],
      newestFirst,
    };
  }).sort((a, b) => a.unit.localeCompare(b.unit, undefined, { numeric: true }));
}

/** One row per vehicle type, with its units underneath. */
export function byVehicleType(rows: QuoteOfferCallRow[]) {
  return Array.from(groupBy(rows, r => r.VehicleType?.trim() || 'Unknown Type'), ([type, calls]) => {
    const units = byUnit(calls);
    return { type, ...totals(calls), unitCount: units.length, units };
  }).sort((a, b) => b.calls - a.calls || a.type.localeCompare(b.type));
}

export function byHour(rows: QuoteOfferCallRow[]) {
  const hours = Array.from({ length: 24 }, (_, hour) => ({ hour, calls: 0, reached: 0 }));
  rows.forEach(r => {
    const h = hours[dayjs(r.CreatedUtc).hour()];
    h.calls += 1;
    if (stageOf(r) === 'reached') h.reached += 1;
  });
  return hours.map(h => ({ ...h, rate: pct(h.reached, h.calls) }));
}

export function byDay(rows: QuoteOfferCallRow[], from: Date, to: Date) {
  const days: { day: string; accepted: number; declined: number; otherReached: number; notReached: number }[] = [];
  for (let d = dayjs(from).startOf('day'); d.valueOf() <= dayjs(to).startOf('day').valueOf(); d = d.add(1, 'day')) {
    days.push({ day: d.format('YYYY-MM-DD'), accepted: 0, declined: 0, otherReached: 0, notReached: 0 });
  }
  const index = new Map(days.map(d => [d.day, d]));
  rows.forEach(r => {
    const d = index.get(dayjs(r.CreatedUtc).format('YYYY-MM-DD'));
    if (!d) return;
    if (stageOf(r) !== 'reached') d.notReached += 1;
    else if (isAccepted(r)) d.accepted += 1;
    else if (r.Response === RESPONSE.Declined) d.declined += 1;
    else d.otherReached += 1;
  });
  return days;
}

const STALE_MINUTES = 15;

export function attentionIssue(r: QuoteOfferCallRow, now: Date): { label: string; detail: string } | null {
  const stage = stageOf(r);
  if (stage === 'failed') return { label: 'Failed', detail: r.ResponseDetail || r.DisconnectionReason || 'Call could not be placed' };
  if (r.ResponseDetail) return { label: 'Not applied', detail: r.ResponseDetail };
  if (r.Response === RESPONSE.WrongNumber) return { label: 'Wrong number', detail: 'The person who answered was not the driver' };
  if (r.Response === RESPONSE.Unclear) return { label: 'Unclear', detail: 'No clear answer on the call' };
  if (stage === 'pending' && dayjs(now).diff(dayjs(r.CreatedUtc), 'minute') >= STALE_MINUTES) {
    return { label: 'No result', detail: 'Retell never reported how the call ended' };
  }
  return null;
}
